import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  User, CreditCard, MapPin, CheckCircle, XCircle, Clock,
  Eye, MoreHorizontal, Download, FileDown, AlertTriangle, Zap, Search, X, Calculator,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import PayrollEntrySkeleton from './PayrollEntrySkeleton';

const PayrollEntriesCard = ({
  loading,
  entries,
  filteredEntries,
  showAllEntries,
  setShowAllEntries,
  selectedEntries,
  handleSelectAll,
  handleSelectEntry,
  formatCurrency,
  handleShowEntryDetails,
  handleGeneratePDF,
  handleBulkExport,
  handleBulkApprove,
  handleBulkReject,
  setSelectedEntries,
  handleCalculatePeriod,
  calculating,
  periodClosed = false,
  setSearchTerm,
  setStatusFilter,
  setDepartmentFilter,
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              <div className="w-32 h-5 bg-muted rounded animate-pulse" />
            </div>
            <div className="w-24 h-4 bg-muted rounded animate-pulse" />
          </div>
        </CardHeader>
        <CardContent>
          <PayrollEntrySkeleton />
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-16">
          <div className="p-4 bg-muted rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <Calculator className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">{t('payrollUi.entries.noCalc')}</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t('payrollUi.entries.noCalcSub')}
          </p>
          <Button
            onClick={handleCalculatePeriod}
            disabled={calculating || periodClosed}
            title={periodClosed ? t('payrollUi.header.periodClosed') : undefined}
            className="px-8 py-3"
            style={{ backgroundColor: "#5e6ad2", color: "#ffffff" }}
          >
            <Zap className="h-5 w-5 mr-2" />
            {periodClosed ? t('payrollUi.header.periodClosed') : t('payrollUi.entries.calculate')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (filteredEntries.length === 0) {
    return (
      <Card style={{ borderColor: "rgba(245,158,11,0.25)" }}>
        <CardContent className="text-center py-12">
          <Search className="h-12 w-12 text-amber-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-amber-400 mb-2">{t('payrollUi.entries.noResults')}</h3>
          <p className="text-amber-400 mb-4">{t('payrollUi.entries.noResultsSub')}</p>
          <Button
            variant="outline"
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); setDepartmentFilter('all'); }}
            className="border-amber-900/40 text-amber-400 hover:bg-amber-950/20"
          >
            <X className="h-4 w-4 mr-2" />
            {t('payrollUi.entries.resetFilters')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader style={{ backgroundColor: "var(--lm-s2)", borderBottom: "1px solid var(--lm-border)" }}>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3">
              <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.12)" }}>
                <User className="h-4 w-4" style={{ color: "#5e6ad2" }} />
              </div>
              <div>
                <span className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
                  {t('payrollUi.entries.title')} ({filteredEntries.length}
                  {filteredEntries.length !== entries.length && ` ${t('payrollUi.entries.ofCount', { total: entries.length })}`})
                </span>
                <p className="text-xs font-normal mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{t('payrollUi.entries.subtitle')}</p>
              </div>
            </CardTitle>

            {filteredEntries.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                  onCheckedChange={handleSelectAll}
                  className="border-white data-[state=checked]:bg-card data-[state=checked]:text-foreground"
                />
                <label className="text-sm opacity-90 cursor-pointer">{t('payrollUi.entries.selectAll')}</label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {(showAllEntries ? filteredEntries : filteredEntries.slice(0, 12)).map((entry, index) => (
              <div
                key={entry.id}
                className="group flex items-center justify-between p-6 transition-colors"
                style={{
                  backgroundColor: selectedEntries.includes(entry.id) ? "rgba(94,106,210,0.08)" : "transparent",
                  borderLeft: selectedEntries.includes(entry.id) ? "2px solid #5e6ad2" : "2px solid transparent",
                  animationDelay: `${index * 0.05}s`,
                }}
              >
                <div className="flex items-center gap-4">
                  <Checkbox
                    checked={selectedEntries.includes(entry.id)}
                    onCheckedChange={(checked) => handleSelectEntry(entry.id, checked)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />

                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div
                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                          entry.is_approved ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
                        }`}
                      >
                        {entry.is_approved ? '✓' : '!'}
                      </div>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground group-hover:text-foreground">
                        {entry.employee_full_name}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <CreditCard className="h-3 w-3" />
                        {entry.employee_registration_number}
                        <span className="text-muted-foreground">•</span>
                        <MapPin className="h-3 w-3" />
                        {entry.employee_department || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center min-w-[60px]">
                    <p className="font-bold text-muted-foreground">{entry.total_worked_hours || 0}h</p>
                    <p className="text-xs text-muted-foreground">{t('payrollUi.entries.hours')}</p>
                  </div>
                  <div className="text-center min-w-[80px]">
                    <p className="font-bold text-muted-foreground">{formatCurrency(entry.gross_pay)}</p>
                    <p className="text-xs text-muted-foreground">{t('payrollUi.entries.gross')}</p>
                  </div>
                  <div className="text-center min-w-[80px]">
                    <p className="font-bold text-red-600">-{formatCurrency(entry.total_deductions)}</p>
                    <p className="text-xs text-muted-foreground">{t('payrollUi.entries.deductions')}</p>
                  </div>
                  <div className="text-center min-w-[90px]">
                    <p className="font-bold text-green-600 text-base">{formatCurrency(entry.net_pay)}</p>
                    <p className="text-xs text-muted-foreground">{t('payrollUi.entries.netPay')}</p>
                  </div>

                  <Badge
                    variant={entry.is_approved ? 'success' : 'secondary'}
                    className={`${entry.is_approved ? 'bg-green-950/30 text-green-400 border-green-900/40' : 'bg-amber-950/30 text-amber-400 border-amber-900/40'} px-3 py-1`}
                  >
                    {entry.is_approved ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> {t('payrollUi.entries.approved')}</>
                    ) : (
                      <><Clock className="h-3 w-3 mr-1" /> {t('payrollUi.entries.pending')}</>
                    )}
                  </Badge>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => handleShowEntryDetails(entry)} className="gap-2">
                        <Eye className="h-4 w-4" />
                        {t('payrollUi.entries.seeDetails')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleGeneratePDF(entry)}
                        className="gap-2 bg-red-950/20 text-red-400 font-semibold"
                      >
                        <FileDown className="h-4 w-4 text-red-400" />
                        {t('payrollUi.entries.officialPdf')}
                        <span className="ml-auto text-xs bg-red-900/40 text-red-300 px-2 py-0.5 rounded-full">TN</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleBulkExport([entry.id])} className="gap-2">
                        <Download className="h-4 w-4" />
                        {t('payrollUi.entries.downloadCsv')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {!entry.is_approved ? (
                        <>
                          <DropdownMenuItem
                            onClick={() => handleBulkApprove([entry.id])}
                            className="text-green-500 gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            {t('payrollUi.entries.approve')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleBulkReject([entry.id])}
                            className="text-red-500 gap-2"
                          >
                            <XCircle className="h-4 w-4" />
                            {t('payrollUi.entries.reject')}
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem className="text-muted-foreground cursor-not-allowed gap-2">
                          <CheckCircle className="h-4 w-4" />
                          {t('payrollUi.entries.alreadyApproved')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}

            {filteredEntries.length > 12 && !showAllEntries && (
              <div className="text-center py-6 bg-muted/20 border-t border-border">
                <p className="text-sm text-muted-foreground font-medium">
                  {t('payrollUi.entries.andOthers', { count: filteredEntries.length - 12 })}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setShowAllEntries(true)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t('payrollUi.entries.seeAll', { count: filteredEntries.length })}
                </Button>
              </div>
            )}

            {showAllEntries && filteredEntries.length > 12 && (
              <div className="text-center py-4 bg-muted/20 border-t border-border">
                <p className="text-sm text-muted-foreground font-medium mb-2">
                  {t('payrollUi.entries.fullDisplay', { count: filteredEntries.length })}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllEntries(false)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t('payrollUi.entries.condensedDisplay')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {entries.some((e) => !e.is_approved) && (
        <Alert className="border-amber-900/40 bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <AlertDescription className="text-amber-400">
            <span className="font-semibold">
              {t('payrollUi.entries.pendingAlert', { count: entries.filter((e) => !e.is_approved).length })}
            </span>
            <span className="ml-2">{t('payrollUi.entries.checkBeforeFinal')}</span>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};

export default PayrollEntriesCard;
